import type { ComponentProps, Ref, RefObject } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
// eslint-disable-next-line no-restricted-imports
import type { PrefetchBehavior, Link as RRDLink } from 'react-router';
import { UNSAFE_DataRouterContext, matchRoutes } from 'react-router';

// Compiler can't handle polymorphic type so we'll need a helper function
// https://github.com/facebook/react/issues/29832
const updateRef = <T,>(ref: RefObject<T>, value: T) => {
  ref.current = value;
};
const useMergeRefs = <T,>(ref1: Nullable<Ref<T>>, ref2: Nullable<Ref<T>>) => {
  return useCallback(
    (instance: T) => {
      if (ref1 != null) {
        if (typeof ref1 === 'function') {
          ref1(instance);
        } else {
          updateRef(ref1, instance);
        }
      }
      if (ref2 != null) {
        if (typeof ref2 === 'function') {
          ref2(instance);
        } else {
          updateRef(ref2, instance);
        }
      }
    },
    [ref1, ref2]
  );
};

type PreloadRoute =
  | {
      routeToFile: Record<string, string>;
      fileToImports: Record<string, string[]>;
    }
  | undefined;

function unique<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

function getFilesFromMatch(
  matches: NonNullable<ReturnType<typeof matchRoutes>>
) {
  return unique(
    matches.flatMap((match, i) =>
      [
        i === matches.length - 1 ? match.route.handle?.page : null,
        match.route.handle?.layout,
      ].filter(Boolean)
    )
  );
}

export const usePrefetch = <T extends HTMLElement>(
  ref?: Ref<T>,
  prefetch: PrefetchBehavior = 'intent'
) => {
  const innerRef = useRef<T>(null);
  const mergedRef = useMergeRefs(ref, innerRef);
  const [shouldPrefetch, setShouldPrefetch] = useState(false);

  useEffect(() => {
    const node = innerRef.current;
    if (node && prefetch === 'intent') {
      const cb = () => {
        setShouldPrefetch(true);
      };
      node.addEventListener('pointerenter', cb);
      return () => node.removeEventListener('pointerenter', cb);
    }
    return () => {};
  }, [prefetch]);

  return [mergedRef, prefetch === 'render' ? true : shouldPrefetch] as const;
};

export const PrefetchLink = ({
  to,
}: Pick<ComponentProps<typeof RRDLink>, 'to'>) => {
  const { router } = useContext(UNSAFE_DataRouterContext) ?? {};
  // @ts-expect-error preloadRoute
  const { preloadRoutes }: { preloadRoutes: PreloadRoute } = window;
  if (preloadRoutes && router) {
    const match = matchRoutes(router.routes, to);
    if (match) {
      const files = getFilesFromMatch(match);
      const outputFiles = files.map(file => preloadRoutes.routeToFile[file]);
      const imports = new Set(outputFiles);
      for (const f of outputFiles) {
        for (const i of preloadRoutes.fileToImports[f]) {
          imports.add(i);
        }
      }
      return (
        <Helmet>
          {[...imports].map(e => (
            <link key={e} href={`/${e}`} rel="modulepreload" />
          ))}
        </Helmet>
      );
    }
  }
  return null;
};
